// What each subscription tier includes.
//
// This used to be a mirror of the BITMASK in api/src/lib/feature-flags.js: a
// map of feature names to bits, plus a copy of the tier table, with a comment
// saying "change one, change the other". It had already drifted, still listing
// RESERVATION_LINK under premium after that feature was made free.
//
// Tier is the entitlement now, so there are no bits here and nothing to keep
// in step bit-for-bit. This is still a mirror of the tier assignments in
// api/src/lib/feature-catalog.js, which the API also serves at
// `industry/feature-catalog`; loading it from there would remove the last of
// the duplication and is the obvious next step.

// What each tier ADDS to the one before it. Made cumulative below, so a higher
// tier can never grant less than a lower one.
const TIER_ADDITIONS = [
    ["claimed", []],
    ["enhanced", ["SHORT_DESCRIPTION", "WELCOME_MESSAGE", "LISTING_QR_CODE"]],
    ["premium", ["LOGO", "WINE_CLUB_PROMOTION", "UPLOAD_GALLERY_PHOTOS",
                 "CUSTOM_FLIGHT_COVER", "MODERATE_USER_PHOTOS",
                 "ADVANCED_ANALYTICS", "ADVANCED_PASSPORT_ANALYTICS"]],
    ["reserve", ["REMOVE_NEARBY"]],
];

// Features deliberately available to every listing, including unpaid ones.
// Listed so that "this has no gate" is a statement rather than an absence
// somebody later reads as an oversight and helpfully corrects.
export const UNGATED = ["RESERVATION_LINK"];

export const TIER_LABELS = {
    claimed: "Claimed",
    enhanced: "Enhanced",
    premium: "Premium",
    reserve: "Reserve",
};

export const TIER_ORDER = TIER_ADDITIONS.map(([name]) => name);

// tier -> Set of every feature it includes, accumulated down the list.
const TIER_FEATURES = (() => {
    const out = {};
    const acc = [];
    for (const [tier, additions] of TIER_ADDITIONS) {
        acc.push(...additions);
        out[tier] = new Set(acc);
    }
    return out;
})();

/**
 * Does this tier include the feature?
 *
 * An unknown tier grants nothing, which fails closed. An ungated feature is
 * granted to everyone, including a listing with no tier at all.
 */
export function tierHasFeature(tier, feature) {
    if (UNGATED.includes(feature)) return true;
    const features = TIER_FEATURES[tier];
    return features ? features.has(feature) : false;
}

/** The cheapest tier that includes this feature, or null if none gates it. */
export function tierForFeature(feature) {
    if (UNGATED.includes(feature)) return null;
    for (const [tier, additions] of TIER_ADDITIONS) {
        if (additions.includes(feature)) return tier;
    }
    return null;
}

export default TIER_FEATURES;
